import React, { useEffect, useState, useRef, useCallback } from "react";
import { NavLink } from "react-router-dom";
import {
  Target,
  ClipboardList,
  Mail,
  Bell,
  TrendingUp,
  Zap,
  ArrowRight,
  Phone,
} from "lucide-react";
import HomeNewBannerImg from "../../assets/images/homePage/home-banner-img2.png";

/* ─── Animated Counter Hook ─── */
function useCounter(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

const HERO_PHRASES = [
  "Hospitality Process",
  "Travel Business",
  "Tech Business",
  "Recruitment Process",
  "Financial Services",
];

/* ─── Looping Typewriter Hook for rotating hero phrases ─── */
function useRotatingTypewriter(
  phrases,
  typingSpeed = 65,
  deletingSpeed = 35,
  pauseDuration = 1800,
) {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;
    const currentPhrase = phrases[phraseIndex] || "";

    if (!isDeleting) {
      if (currentText.length < currentPhrase.length) {
        timer = setTimeout(() => {
          setCurrentText(currentPhrase.slice(0, currentText.length + 1));
        }, typingSpeed);
      } else {
        timer = setTimeout(() => {
          setIsDeleting(true);
        }, pauseDuration);
      }
    } else {
      if (currentText.length > 0) {
        timer = setTimeout(() => {
          setCurrentText(currentPhrase.slice(0, currentText.length - 1));
        }, deletingSpeed);
      } else {
        setIsDeleting(false);
        setPhraseIndex((prev) => (prev + 1) % phrases.length);
      }
    }

    return () => clearTimeout(timer);
  }, [
    currentText,
    isDeleting,
    phraseIndex,
    phrases,
    typingSpeed,
    deletingSpeed,
    pauseDuration,
  ]);

  return currentText;
}

/* ─── Typewriter Hook ─── */
function useTypewriter(text, speed = 28, startDelay = 400) {
  const [displayed, setDisplayed] = useState("");
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStarted(true), startDelay);
    return () => clearTimeout(t);
  }, [startDelay]);

  useEffect(() => {
    if (!started) return;
    let i = 0;
    setDisplayed("");
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [started, text, speed]);

  return { displayed, done: displayed.length === text.length };
}

const Banner = () => {
  const [mounted, setMounted] = useState(false);
  const [statsStarted, setStatsStarted] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    const s = setTimeout(() => setStatsStarted(true), 900);
    return () => { clearTimeout(t); clearTimeout(s); };
  }, []);

  /* Mouse parallax — computed JS values, must stay inline on the image wrapper */
  const handleMouseMove = useCallback((e) => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / rect.width;
    const dy = (e.clientY - cy) / rect.height;
    setTilt({ x: dy * -10, y: dx * 10 });
  }, []);

  const handleMouseLeave = useCallback(() => setTilt({ x: 0, y: 0 }), []);

  /* Live counters */
  const leads  = useCounter(3,   1400, statsStarted);
  const time   = useCounter(85,  1600, statsStarted);
  const hotels = useCounter(500, 1800, statsStarted);

  const pills = [
    { label: "Lead Management",   icon: <Target className="tw-w-[18px] tw-h-[18px] tw-text-[#5D44FF]" /> },
    { label: "Web-to-Lead Forms", icon: <ClipboardList className="tw-w-[18px] tw-h-[18px] tw-text-indigo-500" /> },
    { label: "1:1 Email",         icon: <Mail className="tw-w-[18px] tw-h-[18px] tw-text-violet-500" /> },
    { label: "Real-time Alerts",  icon: <Bell className="tw-w-[18px] tw-h-[18px] tw-text-amber-500" /> },
  ];

  const pillDelays = ["anim-delay-900", "anim-delay-980", "anim-delay-1060", "anim-delay-1140"];

  const typedHeadline = useRotatingTypewriter(HERO_PHRASES, 65, 35, 1800);

  const description =
    "Streamline lead management, build your reputation, and grow your hospitality business with our all-in-one sales automation platform.";
  const { displayed: typedDesc, done: typeDone } = useTypewriter(description, 22, 700);

  return (
    <section
      className="tw-relative tw-overflow-hidden tw-bg-white tw-py-16 lg:tw-py-24"
      id="home"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* ── Contact pill — top right of hero ── */}
      <div className="tw-hidden lg:tw-flex tw-absolute tw-top-5 tw-right-6 tw-z-20 tw-items-center tw-gap-0 tw-bg-white tw-border tw-border-solid tw-border-slate-200 tw-rounded-full tw-px-5 tw-py-2 tw-shadow-sm">
        <a
          href="tel:+91-9625-981200"
          className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-text-[#5D44FF] tw-transition-colors tw-pr-4 tw-no-underline"
        >
          <Phone className="tw-h-4 tw-w-4 tw-text-slate-500" />
          +91-9625-981200
        </a>
        <div className="tw-w-px tw-h-4 tw-bg-slate-300 tw-flex-shrink-0" />
        <a
          href="mailto:contact@beyondleadz.ai"
          className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-text-[#5D44FF] tw-transition-colors tw-pl-4 tw-no-underline"
        >
          <Mail className="tw-h-4 tw-w-4 tw-text-slate-500" />
          contact@beyondleadz.ai
        </a>
      </div>

      {/* ── Main Content ── */}
      <div className="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-relative tw-z-10">
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-12 tw-items-center">

          {/* ─── Left Column ─── */}
          <div className="lg:tw-col-span-7 tw-flex tw-flex-col tw-text-left">

            {/* Live badge */}
            <div
              className={`tw-inline-flex tw-items-center tw-gap-2 tw-mb-6 tw-self-start tw-px-3.5 tw-py-1.5 tw-rounded-full tw-border tw-border-solid tw-border-[#5D44FF]/20 tw-bg-[#5D44FF]/5 tw-text-[#5D44FF] tw-text-xs tw-font-semibold tw-tracking-wide anim-delay-0 ${mounted ? "animate-badge-bounce" : "tw-opacity-0"}`}
            >
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-emerald-400 animate-dot-pulse" />
              Now with AI-Powered Insights
            </div>

            {/* Headline — Dynamic Typing Effect */}
            <h1 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-extrabold tw-tracking-tight tw-leading-[1.15] tw-min-h-[75px] sm:tw-min-h-[105px] lg:tw-min-h-[125px]">
              <span className="tw-block tw-text-slate-900">Automate Your</span>
              <span className="tw-text-[#5D44FF] tw-inline-flex tw-items-center">
                <span>{typedHeadline}</span>
                <span className="hero-cursor !tw-h-[0.9em] !tw-w-[3px] !tw-ml-1.5" />
              </span>
            </h1>

            {/* Typewriter Description */}
            <div className="tw-text-lg tw-text-slate-600 tw-leading-relaxed tw-mb-8 tw-max-w-xl tw-min-h-[120px] sm:tw-min-h-[80px]">
              {typedDesc}
              {/* hero-cursor defined in index.css */}
              {!typeDone && <span className="hero-cursor" />}
            </div>

            {/* Feature Pills */}
            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-8">
              {pills.map((pill, idx) => (
                <span
                  key={idx}
                  className={`tw-inline-flex tw-items-center tw-gap-2 tw-px-3.5 tw-py-1.5 tw-bg-white tw-border tw-border-solid tw-border-[#e3e6f0] tw-rounded-[16px] tw-text-[12.5px] tw-font-medium tw-text-[#333] tw-transition-all tw-cursor-default hover:tw-bg-indigo-50 hover:tw-border-[#5D44FF]/30 hover:tw-text-[#5D44FF] hover:tw-scale-105 ${pillDelays[idx]} ${mounted ? "animate-pill-pop" : "tw-opacity-0"}`}
                >
                  <span className="tw-flex tw-items-center">{pill.icon}</span>
                  {pill.label}
                </span>
              ))}
            </div>

            {/* CTA Buttons */}
            <div
              className={`tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-mb-10 anim-delay-1100 ${mounted ? "animate-fade-in-up" : "tw-opacity-0"}`}
            >
              <NavLink
                to="/signup"
                className="hero-btn-shine tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-7 tw-py-3.5 tw-bg-[#5D44FF] hover:tw-bg-indigo-600 tw-text-white tw-font-bold tw-text-sm tw-rounded-xl tw-shadow-lg tw-shadow-indigo-500/20 tw-transition-all hover:tw-scale-[1.03]"
              >
                Subscribe Now
                <ArrowRight className="tw-w-4 tw-h-4" />
              </NavLink>
              <NavLink
                to="/contact-us"
                className="tw-inline-flex tw-items-center tw-justify-center tw-px-7 tw-py-3.5 tw-bg-transparent tw-border-2 tw-border-solid tw-border-[#5D44FF] hover:tw-bg-indigo-50 tw-text-[#5D44FF] tw-font-bold tw-text-sm tw-rounded-xl tw-transition-all hover:tw-scale-[1.03]"
              >
                Contact us
              </NavLink>
            </div>

            {/* Live-counting Stats */}
            <div className="tw-flex tw-items-center tw-gap-4 sm:tw-gap-8">
              {[
                { count: `${leads}×`,  label: "More Leads",  delayClass: "anim-delay-900"  },
                { count: `${time}%`,   label: "Time Saved",  delayClass: "anim-delay-1020" },
                { count: `${hotels}+`, label: "Hotels",      delayClass: "anim-delay-1140" },
              ].map((s, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <div className="tw-w-px tw-h-8 tw-bg-slate-200 tw-flex-shrink-0" />}
                  <div className={`tw-flex tw-flex-col ${s.delayClass} ${statsStarted ? "hero-counter-up" : "tw-opacity-0"}`}>
                    <span className="tw-text-2xl tw-font-extrabold tw-text-[#5D44FF] tw-tabular-nums">
                      {s.count}
                    </span>
                    <span className="tw-text-xs tw-text-slate-500 tw-font-medium tw-mt-0.5">
                      {s.label}
                    </span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ─── Right Column: Hero Image with Mouse Parallax ─── */}
          <div
            className={`lg:tw-col-span-5 tw-flex tw-justify-center tw-relative anim-delay-300 ${mounted ? "animate-scale-in" : "tw-opacity-0"}`}
            ref={imageRef}
          >
            {/* hero-img-glow defined in index.css */}
            <div className="hero-img-glow" />

            {/* Rotating dashed ring — hero-ring-size in index.css */}
            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none" style={{ zIndex: 0 }}>
              <div className="hero-ring-rotate hero-ring-size tw-rounded-full tw-border-2 tw-border-dashed tw-border-[#5D44FF]/15" />
            </div>

            {/* Image with 3D tilt — tilt.x / tilt.y are dynamic JS: must stay inline */}
            <div
              className="tw-relative tw-z-10 animate-float"
              style={{
                transform: `perspective(900px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
                transition: "transform 0.15s ease-out",
                willChange: "transform",
              }}
            >
              {/* img-shadow-indigo defined in index.css */}
              <img
                src={HomeNewBannerImg}
                className="tw-max-w-full tw-h-auto tw-object-contain img-shadow-indigo"
                alt="Double Your Qualified Leads"
              />
            </div>

            {/* Floating badge — top left */}
            <div
              className={`tw-absolute tw-top-4 tw-left-0 tw-z-20 tw-bg-white tw-rounded-2xl tw-shadow-xl tw-shadow-slate-200/60 tw-px-3 tw-py-2 sm:tw-px-4 sm:tw-py-3 tw-flex tw-items-center tw-gap-2.5 sm:tw-gap-3 tw-border tw-border-solid tw-border-slate-100 anim-delay-1200 ${mounted ? "animate-badge-bounce" : "tw-opacity-0"}`}
            >
              <div className="tw-w-8 tw-h-8 sm:tw-w-9 sm:tw-h-9 tw-rounded-xl tw-bg-emerald-50 tw-flex tw-items-center tw-justify-center">
                <TrendingUp className="tw-w-5 tw-h-5 tw-text-emerald-600" />
              </div>
              <div>
                <p className="tw-text-[11px] sm:tw-text-xs tw-font-bold tw-text-slate-800 tw-m-0">Leads ↑ 3×</p>
                <p className="tw-text-[9px] sm:tw-text-[10px] tw-text-slate-400 tw-m-0">This Month</p>
              </div>
            </div>

            {/* Floating badge — bottom right */}
            <div
              className={`tw-absolute tw-bottom-4 tw-right-0 tw-z-20 tw-bg-white tw-rounded-2xl tw-shadow-xl tw-shadow-slate-200/60 tw-px-3 tw-py-2 sm:tw-px-4 sm:tw-py-3 tw-flex tw-items-center tw-gap-2.5 sm:tw-gap-3 tw-border tw-border-solid tw-border-slate-100 anim-delay-1380 ${mounted ? "animate-badge-bounce" : "tw-opacity-0"}`}
            >
              <div className="tw-w-8 tw-h-8 sm:tw-w-9 sm:tw-h-9 tw-rounded-xl tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center">
                <Zap className="tw-w-5 tw-h-5 tw-text-indigo-600" />
              </div>
              <div>
                <p className="tw-text-[11px] sm:tw-text-xs tw-font-bold tw-text-slate-800 tw-m-0">Automated</p>
                <p className="tw-text-[9px] sm:tw-text-[10px] tw-text-slate-400 tw-m-0">500+ Hotels</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Banner;
